import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { BellRing, Send, Loader2, Radio, Trash2, Plus, LayoutTemplate } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const inputCls = "bg-background border-border rounded-sm text-sm focus-visible:ring-1 focus-visible:ring-primary";

function base64ToUint8Array(value) {
  const padding = "=".repeat((4 - (value.length % 4)) % 4);
  const base64 = (value + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  return Uint8Array.from([...raw].map((c) => c.charCodeAt(0)));
}

export default function PushNotifications() {
  const qc = useQueryClient();
  const [enabling, setEnabling] = useState(false);
  const [sending, setSending] = useState(false);
  const [target, setTarget] = useState("All");
  const [subProduct, setSubProduct] = useState("The Well");
  const [msg, setMsg] = useState({ title: "Precipice Forge", body: "This is a test push from the console." });
  const [tpl, setTpl] = useState({ name: "", title: "", body: "", product: "All" });
  const [savingTpl, setSavingTpl] = useState(false);

  const { data: subData } = useQuery({ queryKey: ["push-subs"], queryFn: async () => (await api.get("/push/subscriptions")).data });
  const { data: prodData } = useQuery({ queryKey: ["push-products"], queryFn: async () => (await api.get("/push/products")).data });
  const { data: tplData } = useQuery({ queryKey: ["push-templates"], queryFn: async () => (await api.get("/push/templates")).data });

  const subs = subData?.subscriptions || [];
  const products = prodData?.products || ["All", "The Well"];
  const templates = tplData?.templates || [];
  const targetCount = target === "All" ? subs.length : subs.filter((s) => s.product === target).length;

  const enable = async () => {
    setEnabling(true);
    try {
      if (!("serviceWorker" in navigator) || !("PushManager" in window))
        throw new Error("This browser does not support Web Push.");
      const permission = await Notification.requestPermission();
      if (permission !== "granted") throw new Error("Notification permission denied.");
      const registration = await navigator.serviceWorker.register("/sw.js");
      await navigator.serviceWorker.ready;
      const { data: pk } = await api.get("/push/public-key");
      let sub = await registration.pushManager.getSubscription();
      if (!sub)
        sub = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: base64ToUint8Array(pk.public_key),
        });
      await api.post("/push/subscribe", { ...sub.toJSON(), product: subProduct });
      toast.success(`Notifications enabled for ${subProduct}`);
      qc.invalidateQueries({ queryKey: ["push-subs"] });
      qc.invalidateQueries({ queryKey: ["push-products"] });
    } catch (e) {
      toast.error(e.message || "Could not enable notifications");
    } finally {
      setEnabling(false);
    }
  };

  const send = async (payload, label) => {
    setSending(true);
    try {
      const { data: res } = await api.post("/push/test", payload);
      toast.success(`${label} · ${res.sent} delivered, ${res.failed} failed`);
      qc.invalidateQueries({ queryKey: ["kpis"] });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  const sendTest = () =>
    send(
      { title: msg.title, body: msg.body, url: "/", product: target === "All" ? undefined : target },
      target === "All" ? "Sent to all devices" : `Sent to ${target}`
    );

  const sendTemplate = (t) =>
    send({ template_id: t.id }, `Template "${t.name}" sent`);

  const saveTemplate = async () => {
    if (!tpl.name || !tpl.title || !tpl.body) return toast.error("Name, title and body are required");
    setSavingTpl(true);
    try {
      await api.post("/push/templates", tpl);
      toast.success("Template saved");
      setTpl({ name: "", title: "", body: "", product: "All" });
      qc.invalidateQueries({ queryKey: ["push-templates"] });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSavingTpl(false);
    }
  };

  const deleteTemplate = async (id) => {
    try {
      await api.delete(`/push/templates/${id}`);
      toast.success("Template deleted");
      qc.invalidateQueries({ queryKey: ["push-templates"] });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-4 max-w-5xl">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Enable + send */}
        <div className="space-y-4">
          <div className="border border-border rounded-sm bg-card p-6">
            <div className="flex items-center gap-2 mb-2">
              <BellRing className="w-4 h-4 text-primary" />
              <h3 className="text-sm font-medium">Web Push (VAPID)</h3>
            </div>
            <p className="text-sm text-muted-foreground mb-5 leading-relaxed">
              Register this browser to a product channel to receive encrypted push notifications.
              Requires HTTPS and granted permission.
            </p>
            <div className="flex items-end gap-2">
              <div className="space-y-2 flex-1">
                <Label className="font-mono text-[11px] uppercase tracking-[0.1em] text-muted-foreground">Product channel</Label>
                <Select value={subProduct} onValueChange={setSubProduct}>
                  <SelectTrigger data-testid="sub-product" className={inputCls}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {products.filter((p) => p !== "All").map((p) => (
                      <SelectItem key={p} value={p}>{p}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button data-testid="enable-push-btn" disabled={enabling} onClick={enable}
                className="rounded-sm bg-primary hover:bg-primary/90 transition-colors duration-200 shrink-0">
                {enabling ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Radio className="w-4 h-4 mr-1" />}
                Enable
              </Button>
            </div>
          </div>

          <div className="border border-border rounded-sm bg-card p-6 space-y-4">
            <h3 className="text-sm font-medium">Send Notification</h3>
            <div className="space-y-2">
              <Label className="font-mono text-[11px] uppercase tracking-[0.1em] text-muted-foreground">Target</Label>
              <Select value={target} onValueChange={setTarget}>
                <SelectTrigger data-testid="target-product" className={inputCls}><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="All">All devices</SelectItem>
                  {products.filter((p) => p !== "All").map((p) => (
                    <SelectItem key={p} value={p}>{p} only</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="font-mono text-[11px] uppercase tracking-[0.1em] text-muted-foreground">Title</Label>
              <Input data-testid="push-title" className={inputCls} value={msg.title}
                onChange={(e) => setMsg((m) => ({ ...m, title: e.target.value }))} />
            </div>
            <div className="space-y-2">
              <Label className="font-mono text-[11px] uppercase tracking-[0.1em] text-muted-foreground">Body</Label>
              <Input data-testid="push-body" className={inputCls} value={msg.body}
                onChange={(e) => setMsg((m) => ({ ...m, body: e.target.value }))} />
            </div>
            <Button data-testid="send-push-btn" disabled={sending || targetCount === 0} onClick={sendTest}
              className="rounded-sm bg-primary hover:bg-primary/90 transition-colors duration-200">
              {sending ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Send className="w-4 h-4 mr-1" />}
              Send ({targetCount})
            </Button>
            {targetCount === 0 && (
              <p className="text-xs text-muted-foreground">No devices registered for this target yet.</p>
            )}
          </div>
        </div>

        {/* Subscriptions */}
        <div className="border border-border rounded-sm bg-card p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-medium">Registered Devices</h3>
            <span className="font-mono text-[10px] uppercase tracking-[0.12em] text-muted-foreground">{subs.length}</span>
          </div>
          <div className="space-y-2" data-testid="push-subscriptions">
            {subs.map((s) => (
              <div key={s.id} className="flex items-center justify-between border border-border rounded-sm px-3 py-2.5 bg-background">
                <div className="min-w-0">
                  <div className="font-mono text-xs text-white truncate">{s.host}</div>
                  <div className="font-mono text-[10px] text-muted-foreground">
                    {s.product} · {s.created_at ? new Date(s.created_at).toLocaleString() : ""}
                  </div>
                </div>
                <span className="w-2 h-2 rounded-full bg-emerald-500 pulse-dot shrink-0 ml-3" />
              </div>
            ))}
            {subs.length === 0 && (
              <div className="text-center text-sm text-muted-foreground py-10">No devices registered yet.</div>
            )}
          </div>
        </div>
      </div>

      {/* Templates */}
      <div className="border border-border rounded-sm bg-card p-6">
        <div className="flex items-center gap-2 mb-5">
          <LayoutTemplate className="w-4 h-4 text-primary" />
          <h3 className="text-sm font-medium">Notification Templates</h3>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Create form */}
          <div className="space-y-3" data-testid="template-form">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label className="font-mono text-[11px] uppercase tracking-[0.1em] text-muted-foreground">Name</Label>
                <Input data-testid="tpl-name" className={inputCls} value={tpl.name}
                  onChange={(e) => setTpl((t) => ({ ...t, name: e.target.value }))} placeholder="Breach Alert" />
              </div>
              <div className="space-y-2">
                <Label className="font-mono text-[11px] uppercase tracking-[0.1em] text-muted-foreground">Product</Label>
                <Select value={tpl.product} onValueChange={(v) => setTpl((t) => ({ ...t, product: v }))}>
                  <SelectTrigger data-testid="tpl-product" className={inputCls}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {products.map((p) => (
                      <SelectItem key={p} value={p}>{p === "All" ? "All products" : p}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label className="font-mono text-[11px] uppercase tracking-[0.1em] text-muted-foreground">Title</Label>
              <Input data-testid="tpl-title" className={inputCls} value={tpl.title}
                onChange={(e) => setTpl((t) => ({ ...t, title: e.target.value }))} placeholder="Security Alert" />
            </div>
            <div className="space-y-2">
              <Label className="font-mono text-[11px] uppercase tracking-[0.1em] text-muted-foreground">Body</Label>
              <Input data-testid="tpl-body" className={inputCls} value={tpl.body}
                onChange={(e) => setTpl((t) => ({ ...t, body: e.target.value }))} placeholder="Action required on your account" />
            </div>
            <Button data-testid="save-template-btn" disabled={savingTpl} onClick={saveTemplate}
              className="rounded-sm bg-primary hover:bg-primary/90 transition-colors duration-200">
              {savingTpl ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Plus className="w-4 h-4 mr-1" />} Save Template
            </Button>
          </div>

          {/* Saved templates */}
          <div className="space-y-2" data-testid="template-list">
            {templates.map((t) => (
              <div key={t.id} className="border border-border rounded-sm p-3 bg-background" data-testid="template-row">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-white truncate">{t.name}</span>
                      <span className="font-mono text-[9px] uppercase tracking-[0.08em] px-1.5 py-0.5 rounded-sm border border-border text-muted-foreground">
                        {t.product}
                      </span>
                    </div>
                    <div className="text-xs text-white mt-1">{t.title}</div>
                    <div className="text-xs text-muted-foreground truncate">{t.body}</div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <Button data-testid="template-send" size="sm" disabled={sending} onClick={() => sendTemplate(t)}
                      className="h-8 rounded-sm bg-primary/90 hover:bg-primary text-primary-foreground">
                      <Send className="w-3.5 h-3.5" />
                    </Button>
                    <Button data-testid="template-delete" size="icon" variant="ghost" onClick={() => deleteTemplate(t.id)}
                      className="h-8 w-8 text-muted-foreground hover:text-destructive rounded-sm">
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
            {templates.length === 0 && (
              <div className="text-center text-sm text-muted-foreground py-10 border border-dashed border-border rounded-sm">
                No templates yet. Save one to reuse it.
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
